import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Loader from "components/DesignSystem/Loader/loader";
import Wind from "components/icons/Wind";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { INCORPORATION_FORM_SOURCE } from "constants/incorporation";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useWindowSize } from "hooks/useWindowSize";
import { useDisableContentTopPadding } from "hooks/useDisableContentTopPadding";
import { useState } from "react";
import { useGetFormInstanceTreeForGroupQuery } from "store/apis/incorporationFormBuilder";
import { isStageComplete } from "util/incorporationFormProgress";
import { IncorporationStageDetail } from "../IncorporationFormEditor/IncorporationStageDetail";
import { IncorporationStageSidebar } from "../IncorporationFormEditor/IncorporationStageSidebar";

const IncorporationNoStageEmptyState = () => (
  <div className="t-flex t-h-full t-flex-col t-items-center t-justify-center t-gap-2 t-text-center">
    <Wind className="t-h-6 t-w-6 t-text-text-30" />
    <span className="t-text-subtitle-sm t-text-text-60">
      Nothing to fill out yet
    </span>
    <span className="t-text-body-sm t-text-text-30">
      Your incorporation checklist will show up here once it's ready
    </span>
  </div>
);

export const IncorporationCustomerView = () => {
  usePageTitle("Incorporation");
  const { isMobile } = useWindowSize();
  useDisableContentTopPadding(isMobile);
  const { uuid: groupId } = useCurrentGroupContext();
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);

  const { data, isLoading, isSuccess } = useGetFormInstanceTreeForGroupQuery(
    { groupId },
    { skip: !groupId }
  );

  const stages = [...(data?.stages || [])];

  let lastUnlockedStageId: string | null = null;
  let isPreviousStageComplete = true;
  stages.forEach((stage) => {
    if (isPreviousStageComplete) {
      lastUnlockedStageId = stage.uuid;
    }
    isPreviousStageComplete = isStageComplete(stage);
  });

  const activeStageId = selectedStageId ?? lastUnlockedStageId;
  const activeStageIndex = stages.findIndex(
    (stage) => stage.uuid === activeStageId
  );
  const activeStage = stages[activeStageIndex];
  const previousStage = stages[activeStageIndex - 1];
  const isActiveStageLocked = Boolean(
    previousStage && !isStageComplete(previousStage)
  );
  const isEmpty = stages.length === 0;

  return (
    <DashboardLayout contentProps={{ className: "!t-overflow-hidden" }}>
      <div className="t-flex t-h-full t-min-h-0 t-flex-col">
        <Header v2 title="Incorporation" />

        <div className="t-flex t-min-h-0 t-flex-1 sm:t-flex-col">
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={isEmpty}
            renderOnLoad={
              <div className="t-flex t-h-full t-w-full t-items-center t-justify-center">
                <Loader customType="primary" />
              </div>
            }
          >
            <Async.Empty>
              <EmptyScreen text="Nothing to fill out yet" />
            </Async.Empty>
            <Async.Success>
              <IncorporationStageSidebar
                stages={stages}
                selectedStageId={activeStageId}
                onSelectStage={setSelectedStageId}
                source={INCORPORATION_FORM_SOURCE.INSTANCE}
                parentId={data?.uuid ?? ""}
                isEditable={false}
                isAnswerable
                isPublished
              />

              <div className="t-flex t-h-full t-flex-1 t-flex-col sm:t-min-h-0">
                <div className="t-flex t-min-h-0 t-flex-1 t-justify-center t-overflow-y-auto t-pl-9 t-pr-5 t-pb-5 sm:t-px-0">
                  <div className="t-h-full t-w-full t-max-w-[900px]">
                    {activeStage ? (
                      <IncorporationStageDetail
                        key={activeStage.uuid}
                        stage={activeStage}
                        source={INCORPORATION_FORM_SOURCE.INSTANCE}
                        treeId={data?.uuid ?? ""}
                        isEditable={false}
                        isAnswerable
                        isPublished
                        isLocked={isActiveStageLocked}
                        groupId={groupId}
                      />
                    ) : (
                      <IncorporationNoStageEmptyState />
                    )}
                  </div>
                </div>
              </div>
            </Async.Success>
          </Async.Root>
        </div>
      </div>
    </DashboardLayout>
  );
};
